import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, } from 'react-router-dom';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import ReactDOMServer from 'react-dom/server';
import { bbox, area as turfArea } from '@turf/turf';
import { toast } from 'react-toastify';
import packageInfo from '../../package.json';
import Header from '../components/Header/Header';
import MapViewer from '../components/MapViewer/MapViewer';
import {
    MapIcon,
    LogoutIcon,
    GlobeIcon, 
    LayersIcon, 
    DrawPolygonIcon,
    BuildingIcon,
    RoadIcon,
    RailroadIcon,
    BoundaryIcon,
    PinIcon,
    PlusIcon,
    MinusIcon,
    ViewIcon,
    XCircleIcon,
    ImportIcon,
    ExportIcon,
    RulerIcon,
    CrosshairIcon,
    ListIcon,
    DrawLineIcon,
    SettingsIcon,
    EditIcon,
    TextIcon,
    PrintIcon,
    ExcelIcon,
    InfoIcon,
    SaveIcon,
    CloseIcon,
    TrashIcon,
    CopyIcon
} from '../components/Icon';
import ThemeToggle from '../components/ThemeToggle/ThemeToggle';
import styles from './Pages.module.css';
import EditingPanel from '../components/EditingPanel/EditingPanel';
import LegendManager from '../components/LegendManager/LegendManager';
import CoordsInfoPanel from '../components/CoordsInfoPanel/CoordsInfoPanel';
import ObjectManagerPanel from '../components/ObjectManagerPanel/ObjectManagerPanel';
import ActionToolbar from '../components/ActionToolbar/ActionToolbar';
import ImportModal from '../components/ImportModal/ImportModal';
import ConfirmDialog from '../components/ConfirmDialog/ConfirmDialog';
import { modules } from '../generated-modules';

// Поиск компонентов модулей
const nspdModule = modules.find(m => m.id === 'nspd-integration');
const spdPlusModule = modules.find(m => m.id === 'spd-plus');
const printingModule = modules.find(m => m.id === 'printing');
const NSPDUpdateModal = nspdModule?.UpdateModal || (() => null);
const isSPDPlusActive = !!spdPlusModule;
const TopologyToolbar = spdPlusModule?.TopologyToolbar || (() => null);
const DuplicateInspectorModal = spdPlusModule?.DuplicateInspectorModal || (() => null);
const spdHistoryManager = spdPlusModule?.historyManager;
const spdTopologyEngine = spdPlusModule?.topologyEngine;
const A4PrintLayout = printingModule?.A4PrintLayout;

// Динамический импорт контекстов модулей (если проект сгенерирован)
let usePrinting = () => ({ settings: null });
try {
    const modulePath = '../modules-copy/printing/context/PrintingContext';
    const printingContextModule = require(`${modulePath}`);
    if (printingContextModule) usePrinting = printingContextModule.usePrinting;
} catch (e) {
    // Модуль не установлен или еще не сгенерирован
}

const API_URL = '/api';

// Auth check wrapper - checks auth before rendering component
const useAuthCheck = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch(`${API_URL}/auth/check`, { 
                    credentials: 'include' 
                });
                const data = await response.json();
                if (!data.authenticated) {
                    navigate('/map/admin', { replace: true });
                } else {
                    setIsAuthenticated(true);
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                navigate('/map/admin', { replace: true });
            } finally {
                setIsLoadingAuth(false);
            }
        };
        checkAuth();
    }, [navigate]);

    return { isAuthenticated, isLoadingAuth };
};

// Auth guard component
const AuthGuard = ({ children }) => {
    const { isAuthenticated, isLoadingAuth } = useAuthCheck();

    if (isLoadingAuth) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <p>Проверка авторизации...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return children;
};

const AdminDashboardPageInner = () => {
    const navigate = useNavigate();
    const { settings: printingSettings } = usePrinting();

    // Собираем иконки для передачи в модули (нужно определить раньше для getTools)
    const iconComponents = {
        Map: MapIcon, Logout: LogoutIcon, Globe: GlobeIcon, Layers: LayersIcon, 
        DrawPolygon: DrawPolygonIcon, Building: BuildingIcon, Road: RoadIcon, 
        Railroad: RailroadIcon, Boundary: BoundaryIcon, Pin: PinIcon, Plus: PlusIcon, 
        Minus: MinusIcon, Eye: ViewIcon, EyeOff: XCircleIcon, Import: ImportIcon, 
        Export: ExportIcon, Ruler: RulerIcon, Crosshair: CrosshairIcon, List: ListIcon, 
        DrawLine: DrawLineIcon, Settings: SettingsIcon, Edit: EditIcon, Text: TextIcon, 
        Print: PrintIcon, Excel: ExcelIcon, Info: InfoIcon
    };

    const [activeMode, setActiveMode] = useState('data');
    const [activeTool, setActiveTool] = useState(null);
    const [mapStyleId, setMapStyleId] = useState('custom'); // Изменено на 'custom' по умолчанию
    const [currentMapViewState, setCurrentMapViewState] = useState({
        longitude: parseFloat(process.env.REACT_APP_MAP_LON) || 60.180497,
        latitude: parseFloat(process.env.REACT_APP_MAP_LAT) || 56.469595,
        zoom: parseFloat(process.env.REACT_APP_MAP_ZOOM) || 15.5,
        pitch: parseFloat(process.env.REACT_APP_MAP_PITCH) || 3.5,
        bearing: parseFloat(process.env.REACT_APP_MAP_BEARING) || 62.5
    });
    const [previousMapViewState, setPreviousMapViewState] = useState(null);
    const [restoreMapViewState, setRestoreMapViewState] = useState(null);
    const [editingFeature, setEditingFeature] = useState(null);
    const [featureToEditGeometry, setFeatureToEditGeometry] = useState(null);
    const [featureBeforeEdit, setFeatureBeforeEdit] = useState(null); // Сохраняем исходный объект
    const [drawingControls, setDrawingControls] = useState(null); // State to hold drawing controls from MapViewer
    const mapRef = useRef(null);
    const [featureToCancel, setFeatureToCancel] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [legendData, setLegendData] = useState({
        plots: [],
        buildings: [],
        roads: [],
        markers: []
    });
    const [featureProperties, setFeatureProperties] = useState({});
    // Инициализируем mapData с пустой структурой, чтобы избежать null
    const [mapData, setMapData] = useState({
        type: "FeatureCollection",
        features: []
    });
    const [isLegendManagerVisible, setLegendManagerVisible] = useState(false);
    const [coordsInfo, setCoordsInfo] = useState(null); // Состояние для панели с информацией о координатах
    const [nspdInfo, setNspdInfo] = useState(null); // Состояние для результатов поиска в НСПД по координатам
    const [isNSPDModalVisible, setNSPDModalVisible] = useState(false);
    const [isNSPDUpdateModalOpen, setNSPDUpdateModalOpen] = useState(false);
    const [isObjectManagerVisible, setObjectManagerVisible] = useState(false);
    const [isDuplicateModeActive, setIsDuplicateModeActive] = useState(false);
    const [topologyAction, setTopologyAction] = useState(null); // 'difference' | 'union' | null
    const [duplicatingFeature, setDuplicatingFeature] = useState(null); // Объект в процессе интерактивного дублирования
    const [initialOverlay, setInitialOverlay] = useState(null);
    const allModes = ['drawing', 'data'];
    // Динамически добавляем режимы из модулей
    modules.forEach(mod => {
        if (typeof mod.getTools === 'function') {
            const modTools = mod.getTools(iconComponents); 
            if (modTools && typeof modTools === 'object' && !Array.isArray(modTools)) {
                Object.keys(modTools).forEach(mode => {
                    if (!allModes.includes(mode)) {
                        allModes.push(mode);
                    }
                });
            }
        }
    });
    const [isImportModalVisible, setImportModalVisible] = useState(false);
    const [featuresToImport, setFeaturesToImport] = useState([]);
    const [mapSelectedIds, setMapSelectedIds] = useState([]); // Состояние для выделения на карте через Shift
    const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
    const [isDeletingFeature, setIsDeletingFeature] = useState(false);
    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
    const importInputRef = useRef(null);

    const mobileModes = ['data'];
    const modes = isMobile ? mobileModes : allModes;
    const modeNames = { drawing: 'Рисование', data: 'Данные' };
    
    // Добавляем имена режимов из модулей
    modules.forEach(mod => {
        if (typeof mod.getTools === 'function') {
            const modTools = mod.getTools(iconComponents);
            if (modTools && typeof modTools === 'object' && !Array.isArray(modTools)) {
                Object.keys(modTools).forEach(modeKey => {
                    if (mod.name && !modeNames[modeKey]) {
                        modeNames[modeKey] = mod.name;
                    }
                });
            }
        }
    });

    // --- Управление видимостью объектов ---
    const getSavedVisibilities = useCallback(() => {
        try {
            const saved = localStorage.getItem('featureVisibilities');
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error("Failed to parse feature visibilities from localStorage", e);
            return {};
        }
    }, []);
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            // Если мы на мобильном и активен режим "Рисование", переключаемся на "Данные"
            if (mobile && activeMode === 'drawing') {
                setActiveMode('data');
                setActiveTool(null);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeMode]);

    // Адаптация канваса карты при смене режима (например, переход в А4 лист)
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const map = mapRef.current?.getMap ? mapRef.current.getMap() : mapRef.current;
                if (map && typeof map.resize === 'function') {
                    map.resize();
                }
            } catch (e) {
                console.warn('Map resize error on mode change:', e);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [activeMode]);

    useEffect(() => {
        // Загружаем сохраненные данные из IndexedDB
        const loadOverlay = async () => {
            try {
                const DB_NAME = 'SPD_Overlay_DB';
                const STORE_NAME = 'overlay_store';
                const DB_KEY = 'current_overlay';
                
                const dbRequest = indexedDB.open(DB_NAME, 1);
                dbRequest.onsuccess = (e) => {
                    const db = e.target.result;
                    if (db.objectStoreNames.contains(STORE_NAME)) {
                        const tx = db.transaction(STORE_NAME, 'readonly');
                        const store = tx.objectStore(STORE_NAME);
                        const getRequest = store.get(DB_KEY);
                        getRequest.onsuccess = (ev) => {
                            if (ev.target.result) {
                                setInitialOverlay(ev.target.result);
                            }
                        };
                    }
                };
            } catch (e) {
                console.error("Failed to load overlay from IndexedDB", e);
            }
        };
        loadOverlay();
    }, []);

    const fetchMetadata = useCallback(async () => {
        try {
            console.log('Fetching metadata...');
            const [legendRes, propsRes, mapDataRes] = await Promise.all([
                fetch(`/api/legend?t=${Date.now()}`),
                fetch(`/api/feature-properties?t=${Date.now()}`), // Свойства для панели редактирования
                fetch(`/api/map?t=${Date.now()}`) // Основные данные карты (объекты)
            ]);

            let newLegendData = legendData; // Сохраняем текущую легенду на случай ошибки
            if (legendRes.ok) {
                newLegendData = await legendRes.json();
                setLegendData(newLegendData);
            }

            if (propsRes.ok) {
                const props = await propsRes.json();
                setFeatureProperties(props);
            }

            if (mapDataRes.ok) {
                const mapDataPayload = await mapDataRes.json();
                console.log('Raw mapDataPayload:', mapDataPayload);
                if (mapDataPayload?.features) {
                    console.log('Raw features count:', mapDataPayload.features.length);
                    mapDataPayload.features.forEach((f, i) => {
                        console.log(`Raw feature ${i}: id=${f.id}, type=${f.properties?.type}, name=${f.properties?.name}`);
                    });
                }
                 
                 // Создаем справочников для быстрого доступа к свойствам легенды
                 const categoryProperties = { plot: {}, building: {}, road: {}, line: {}, marker: {} };
                 
                  const mapToDict = (dict, list) => (list || []).forEach(c => { if(c.id) dict[String(c.id)] = c; });
                  mapToDict(categoryProperties.plot, newLegendData.plots);
                  mapToDict(categoryProperties.building, newLegendData.buildings);
                  mapToDict(categoryProperties.road, newLegendData.roads);
                  mapToDict(categoryProperties.line, newLegendData.roads);
                  mapToDict(categoryProperties.marker, newLegendData.markers);

                 // Обновляем свойства объектов в соответствии с новой легендой
                 const updatedFeatures = mapDataPayload.features.map(feature => {
                    // --- Ключевое исправление ---
                    // Гарантируем, что у каждого объекта есть `id` в `properties`.
                    // `promoteId` в MapLibre требует, чтобы ID был именно в `properties`.
                    if (feature.id && feature.properties.id !== feature.id) {
                        feature.properties.id = feature.id;
                    }
                    // --- Конец исправления ---

                     const rawCat = feature.properties.category || feature.properties.marker_id || feature.properties.markerId;
                     const categoryId = (rawCat && typeof rawCat === 'object') ? rawCat.id : rawCat;
                     
                     let type = feature.properties.type;
                     if (!type && feature.id) {
                         if (feature.id.startsWith('plot')) type = 'plot';
                         else if (feature.id.startsWith('building')) type = 'building';
                         else if (feature.id.startsWith('road')) type = 'road';
                         else if (feature.id.startsWith('marker')) type = 'marker';
                     }

                     let newProperties = { ...feature.properties };

                     const props = (categoryId && type && categoryProperties[type]) ? categoryProperties[type][String(categoryId)] : null; if (props) {
                         // Устанавливаем значения с дефолтами, если props.* null/undefined
                         newProperties = {
                             ...newProperties,
                             color: props.color,
                             icon_filename: props.image_filename, // Присваиваем имя файла иконки
                             lineType: (props.lineType !== null && props.lineType !== undefined) ? props.lineType :
                                       (type === 'road' || type === 'line') ? 'solid' : null,
                             hatch: (props.hatch !== null && props.hatch !== undefined) ? props.hatch :
                                    (type === 'plot' || type === 'building') ? 'none' : null
                         };
                     } else {
                         newProperties.color = '#808080'; // Цвет по умолчанию, если категория не найдена
                         // Устанавливаем дефолтные значения для lineType и hatch в зависимости от типа
                         if (type === 'road' || type === 'line') {
                             newProperties.lineType = 'solid';
                             newProperties.hatch = null;
                         } else if (type === 'plot' || type === 'building') {
                             newProperties.hatch = 'none';
                             newProperties.lineType = null;
                         } else if (type === 'marker') {
                            newProperties.icon_filename = null; // Сбрасываем, если категория не найдена
                         } 
                         // Для markers ничего не устанавливаем дополнительно
                     }
                     return { ...feature, properties: newProperties, id: feature.id };
                 });

                // Применяем сохраненные пользователем настройки видимости
                const savedVisibilities = getSavedVisibilities();
                const featuresWithVisibility = updatedFeatures.map(feature => {
                    if (savedVisibilities.hasOwnProperty(feature.id)) {
                        return {
                            ...feature,
                            properties: {
                                ...feature.properties,
                                visible: savedVisibilities[feature.id]
                            }
                        };
                    }
                    return feature;
                });
                // Устанавливаем обновленные данные карты
                setMapData({ ...mapDataPayload, features: featuresWithVisibility });
            }
        } catch (error) {
            console.error('Ошибка загрузки метаданных:', error);
        }
    }, []); // Зависимости не нужны, т.к. функции-сеттеры состояния стабильны

    // Загрузка легенды и метаданных свойств
    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    const mapStyles = ['basic', 'satellite', 'custom'];
    const mapStyleNames = { basic: 'Схема', satellite: 'Спутник', custom: 'Своя' };
    // Генерируем SVG-строки из React-компонентов
    const mapStyleIcons = {
        basic: ReactDOMServer.renderToStaticMarkup(<MapIcon size={18} />),
        satellite: ReactDOMServer.renderToStaticMarkup(<GlobeIcon size={18} />),
        custom: ReactDOMServer.renderToStaticMarkup(<LayersIcon size={18} />)
    };

    const handleMapStyleChange = () => {
        const currentIndex = mapStyles.indexOf(mapStyleId);
        const nextIndex = (currentIndex + 1) % mapStyles.length;
        setMapStyleId(mapStyles[nextIndex]);
    };

    const handleModeChange = () => {
        const currentIndex = modes.indexOf(activeMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        setActiveMode(modes[nextIndex]);
        setActiveTool(null); // Сбрасываем активный инструмент при смене режима
    };

    const handleExport = () => {
        if (!mapData || !mapData.features) {
            toast.warn('Нет данных для экспорта.');
            return;
        }

        const hasSelection = Array.isArray(mapSelectedIds) && mapSelectedIds.length > 0;
        const featuresToExport = hasSelection
            ? mapData.features.filter(f => mapSelectedIds.includes(f.id))
            : mapData.features.filter(f => f.properties.visible !== false);

        if (featuresToExport.length === 0) {
            toast.warn('Нет объектов для экспорта.');
            return;
        }

        const dataToExport = {
            type: 'FeatureCollection',
            features: featuresToExport,
        };

        const jsonString = JSON.stringify(dataToExport, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = hasSelection ? 'selected_map_data.json' : 'map_data.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success(
            hasSelection
                ? `Экспортировано ${featuresToExport.length} выбранных объектов.`
                : `Экспортировано ${featuresToExport.length} объектов.`
        );
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (data && data.type === 'FeatureCollection' && Array.isArray(data.features)) {
                    setFeaturesToImport(data.features);
                    setImportModalVisible(true);
                } else {
                    toast.error('Неверный формат файла. Ожидается GeoJSON FeatureCollection.');
                }
            } catch (error) {
                toast.error('Ошибка при чтении файла: ' + error.message);
            }
        };
        reader.readAsText(file);
        // Сбрасываем значение инпута, чтобы можно было выбрать тот же файл еще раз
        event.target.value = null;
    };

    const handleToolChange = (toolId) => {
        // Обработка кнопки "Помощь" - открытие документации в новой вкладке
        if (toolId === 'help') {
            window.open('/map/admin/documentation', '_blank');
            return;
        }

        // Новая логика: если кликаем на активный инструмент измерения во время редактирования,
        // просто закрываем панель редактирования, но оставляем инструмент активным.
        if (editingFeature && (toolId === 'measure-ruler' || toolId === 'measure-area') && activeTool === toolId) {
            closeAllPanels();
            // Не меняем activeTool, чтобы он остался активным
            return;
        }

        // Определяем, является ли клик первым нажатием на инструмент измерения во время редактирования
        const isFirstMeasureClickWhileEditing = editingFeature && (toolId === 'measure-ruler' || toolId === 'measure-area');

        // Очищаем карту перед сменой инструмента
        if (drawingControls) { // Use the drawingControls state
            drawingControls.clearDrawing(); // Call clearDrawing from the received controls
        }
        // Если мы редактируем объект и переключаемся на инструмент, НЕ являющийся измерительным, отменяем редактирование.
        if (editingFeature && !isFirstMeasureClickWhileEditing) {
            closeAllPanels();
        }

        // Специальная обработка для инструмента легенды
        if (toolId === 'legend') {
            setLegendManagerVisible(true);
            return; // Не устанавливаем activeTool, так как это модальное окно
        }
        // Специальная обработка для менеджера объектов
        if (toolId === 'object-manager') {
            setObjectManagerVisible(prev => !prev);
            return; // Не устанавливаем activeTool
        }

        if (toolId === 'export') {
            handleExport();
            setActiveTool(null); // Экспорт - это одноразовое действие
            return;
        }

        if (toolId === 'import') {
            handleImportClick();
            setActiveTool(null); // Импорт - это одноразовое действие
            return;
        }

        if (toolId === 'nspd-search') {
            setNSPDModalVisible(true);
            return;
        }

        if (toolId === 'spd-plus-duplicates') {
            setObjectManagerVisible(true);
            setIsDuplicateModeActive(true);
            return;
        }

        if (toolId === 'print-svg') {
            const exportA4ToSvg = printingModule?.exportA4ToSvg;
            if (!exportA4ToSvg) {
                toast.error('Функция экспорта в SVG недоступна');
                setActiveTool(null);
                return;
            }

            const map = mapRef?.current?.getMap ? mapRef.current.getMap() : mapRef?.current;
            if (!map) {
                toast.error('Карта не готова к экспорту');
                setActiveTool(null);
                return;
            }

            toast.info('Формирование векторного SVG...');
            exportA4ToSvg({
                map,
                mapData: displayMapData,
                legendData,
                title: document.title || 'Схема генерального плана',
                subtitle: 'Масштабный план территории',
                filename: `ichis_spd_print_${new Date().toISOString().slice(0, 10)}.svg`
            })
                .then(() => {
                    toast.success('Векторный SVG успешно скачан!');
                })
                .catch((err) => {
                    console.error('Ошибка SVG экспорта:', err);
                    toast.error('Ошибка при экспорте в SVG');
                })
                .finally(() => {
                    setActiveTool(null);
                });
            return;
        }

        if (toolId === 'print-pdf') {
            toast.info('Экспорт листа в PDF будет реализован на следующем этапе');
            setActiveTool(null);
            return;
        }

        // Если кликаем на уже активный инструмент, деактивируем его, иначе — активируем новый
        setActiveTool(prevTool => prevTool === toolId ? null : toolId);
        // Если мы деактивируем инструмент координат, закрываем панель
        if (activeTool === 'measure-coords' && activeTool === toolId) {
            setCoordsInfo(null);
        }
    };

    const handleCancelComplete = useCallback(() => {
        setFeatureToCancel(null);
        setPreviousMapViewState(null);
    }, []);

    // Навигация Header: единая кнопка переключения режима
    const navItems = [
        {
            id: 'mode-switcher',
            label: modeNames[activeMode] || activeMode,
            active: true,
            sublabel: 'Режим',
            icon: activeMode === 'drawing' ? <DrawPolygonIcon size={16} /> :
                  activeMode === 'data' ? <LayersIcon size={16} /> :
                  activeMode === 'printing' ? <PrintIcon size={16} /> : <MapIcon size={16} />,
            onClick: handleModeChange
        }
    ];

    const handleLogout = useCallback(async () => {
        try {
            await fetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (err) {
            console.error('Logout error:', err);
        }
        setIsLogoutConfirmOpen(false);
        navigate('/map/admin');
    }, [navigate]);

    // Действия Header
    const headerActions = [
        { 
          id: 'theme-toggle',
          component: <ThemeToggle /> 
        },
        {
            id: 'logout',
            icon: <LogoutIcon size={20} />,
            title: 'Выйти из системы',
            onClick: () => setIsLogoutConfirmOpen(true)
        }
    ];


    // Инструменты для Toolbar в зависимости от режима
    let toolsConfig = {
        drawing: [
            {
                label: 'Рисование',
                items: [
                    { id: 'draw-plot', label: 'Участки', icon: <DrawPolygonIcon size={18} />, title: 'Рисовать участки [Q]' },
                    { id: 'draw-building', label: 'Здания', icon: <BuildingIcon size={18} />, title: 'Рисовать здания [W]' },
                    { id: 'draw-line', label: 'Линии', icon: <DrawLineIcon size={18} />, title: 'Рисовать линейные объекты [E]' },
                    { id: 'draw-marker', label: 'Метки', icon: <PinIcon size={18} />, title: 'Ставить метки [R]' },
                ]
            },
            {
                label: 'Подложка',
                items: [
                    { id: 'basemap', label: 'Подложка', icon: <LayersIcon size={18} />, title: 'Управление подложкой [T]' },
                ]
            },
            {
                label: 'Данные',
                items: [
                    { id: 'import', label: 'Импорт', icon: <ImportIcon size={18} />, title: 'Импорт данных' },
                    { id: 'export', label: 'Экспорт', icon: <ExportIcon size={18} />, title: 'Экспорт данных [V]' },
                ]
            }
        ],
        data: [
            {
                label: 'Измерения',
                items: [
                    { id: 'measure-ruler', label: 'Линейка', icon: <RulerIcon size={18} />, title: 'Измерить расстояние [A]' },
                    { id: 'measure-area', label: 'Площадь', icon: <DrawPolygonIcon size={18} />, title: 'Измерить площадь [S]' },
                    { id: 'measure-coords', label: 'Координаты', icon: <CrosshairIcon size={18} />, title: 'Координаты точки [D]' },
                ]
            },
            {
                label: 'Работа с данными',
                items: [
                    { id: 'legend', label: 'Легенда', icon: <ListIcon size={18} />, title: 'Легенда [G]' },
                    { id: 'object-manager', label: 'Менеджер объектов', icon: <SettingsIcon size={18} />, title: 'Менеджер объектов [F]' },
                ]
            }
        ]
    };

    // Внедряем инструменты из динамических модулей
    modules.forEach(mod => {
        if (typeof mod.getTools === 'function') {
            const modTools = mod.getTools(iconComponents);
            if (modTools && typeof modTools === 'object' && !Array.isArray(modTools)) {
                Object.keys(modTools).forEach(mode => {
                    const groups = Array.isArray(modTools[mode]) ? modTools[mode] : [];
                    if (!toolsConfig[mode]) {
                        toolsConfig[mode] = [...groups];
                    } else if (Array.isArray(toolsConfig[mode])) {
                        toolsConfig[mode] = [...toolsConfig[mode], ...groups];
                    }
                });
            }
        }
    });

    // Добавляем группу "Справка" во все режимы
    const helpGroup = {
        label: 'Справка',
        items: [
            {
                id: 'help',
                label: 'Помощь',
                icon: <InfoIcon size={24} />,
                title: 'Помощь'
            },
        ]
    };
    
    // Добавляем подсветку для кнопки менеджера объектов
    if (toolsConfig.data && Array.isArray(toolsConfig.data) && toolsConfig.data[1] && toolsConfig.data[1].items) {
        const objectManagerButton = toolsConfig.data[1].items.find(item => item.id === 'object-manager');
        if (objectManagerButton) objectManagerButton.active = isObjectManagerVisible;
    }
    Object.keys(toolsConfig).forEach(mode => {
        if (Array.isArray(toolsConfig[mode])) {
            toolsConfig[mode].push(helpGroup);
        }
    });

    const currentMapViewStateRef = React.useRef(currentMapViewState);
    currentMapViewStateRef.current = currentMapViewState;

    const handleFeatureCreate = useCallback((feature) => {
        // --- Исправление: Принудительно устанавливаем тип для новых объектов ---
        if (activeTool && feature.properties) {
            if (activeTool.startsWith('draw-')) {
                const type = activeTool.split('-')[1]; // 'plot', 'building', 'line', 'marker'
                feature.properties.type = type;
            }
        }

        // Если у объекта еще нет категории и цвета, назначаем первую подходящую из легенды
        if (feature.properties && !feature.properties.category && legendData) {
            const type = feature.properties.type;
            let categories = [];
            if (type === 'plot') categories = legendData.plots || [];
            else if (type === 'building') categories = legendData.buildings || [];
            else if (type === 'line' || type === 'road') categories = legendData.roads || [];
            else if (type === 'marker') categories = legendData.markers || [];

            if (categories.length > 0) {
                feature.properties.category = categories[0].id;
                if (categories[0].color && !feature.properties.color) {
                    feature.properties.color = categories[0].color;
                }
            }
        }

        console.log('Feature Created (raw):', feature);
        console.log('Feature id:', feature.id, 'properties.id:', feature.properties?.id);

        // Автоматический расчет площади только при создании нового полигонального объекта (участок, здание),
        // чтобы не перезаписывать вручную указанное значение площади
        if (feature && feature.geometry && (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')) {
            if (!feature.properties) {
                feature.properties = {};
            }
            if (feature.properties.area === undefined || feature.properties.area === null || feature.properties.area === '') {
                try {
                    const calculatedArea = Math.round(turfArea(feature));
                    if (calculatedArea > 0) {
                        feature.properties.area = calculatedArea;
                    }
                } catch (e) {
                    console.error('Error calculating area for new feature:', e);
                }
            }
        }

        setPreviousMapViewState(currentMapViewStateRef.current);
        setEditingFeature(feature);
        setRestoreMapViewState(null); // Сбрасываем флаг восстановления
        setActiveTool(null); // Сбрасываем инструмент после создания
    }, [activeTool, legendData]);

    const previousMapViewStateRef = React.useRef(previousMapViewState);
    previousMapViewStateRef.current = previousMapViewState;

    const handleEditingSave = useCallback((updatedFeature) => {
        console.log('Saving feature - full object:', JSON.stringify(updatedFeature));
        console.log('Saving feature - photos:', JSON.stringify(updatedFeature.properties?.photos));
        console.log('Saving feature - links:', JSON.stringify(updatedFeature.properties?.links));
        console.log('Saving feature - custom_fields:', JSON.stringify(updatedFeature.properties?.custom_fields));

        // Создаем копию объекта для отправки
        const featureToSave = JSON.parse(JSON.stringify(updatedFeature));

        // Если у объекта нет категории, назначаем ему первую доступную из легенды
        const isNewFeature = !mapData?.features.some(f => f.id === featureToSave.id);
        if (isNewFeature && !featureToSave.properties.category && legendData) {
            const type = featureToSave.properties.type;
            let categories = [];
            if (type === 'plot' || type === 'plot') categories = legendData.plots || [];
            else if (type === 'building') categories = legendData.buildings || [];
            else if (type === 'line' || type === 'road') categories = legendData.roads || [];
            else if (type === 'marker') categories = legendData.markers || [];

            if (categories.length > 0) {
                featureToSave.properties.category = categories[0].id;
            }
        }

        // Определяем, является ли объект обновлением существующего (есть ли он в mapData) или созданием нового
        const isUpdate = mapData?.features?.some(f => f.id === featureToSave.id);
        const url = isUpdate ? `/api/feature/${featureToSave.id}` : '/api/feature';
        const method = isUpdate ? 'PUT' : 'POST';

        // Отправляем объект на сервер
        fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(featureToSave)
        })
        .then(async response => {
            if (!response.ok) {
                const errText = await response.text().catch(() => '');
                throw new Error(`Ошибка сохранения (Код: ${response.status}). Ответ: ${errText}`);
            }
            return response.json();
        })
        .then(data => {
            console.log('Feature saved:', data);
            // Очищаем drawRef после успешного сохранения
            if (drawingControls) {
                drawingControls.clearDrawing();
            }
            setEditingFeature(null);
            setFeatureToEditGeometry(null);
            setTopologyAction(null);
            setMapSelectedIds([]);
            toast.dismiss('topo-guide');
            setRestoreMapViewState(previousMapViewState); // Запускаем восстановление предыдущего состояния
            setPreviousMapViewState(null);
            // Обновляем данные карты в состоянии, что вызовет перерисовку MapViewer
            setMapData(prevData => {
                const prevFeatures = prevData?.features || [];
                const existingIndex = prevFeatures.findIndex(f => f.id === data.feature.id);
                let newFeatures;
                if (existingIndex > -1) {
                    newFeatures = [...prevFeatures];
                    newFeatures[existingIndex] = data.feature;
                } else {
                    newFeatures = [...prevFeatures, data.feature];
                }
                // Сохраняем другие поля (overlay, type) из prevData, если они есть; иначе создаём базовый объект
                return {
                    ...(prevData || { type: "FeatureCollection", features: [], overlay: null }),
                    features: newFeatures
                };
            });
        })
        .catch(error => {
            console.error('Ошибка при сохранении:', error);
            toast.error('Не удалось сохранить объект');
        });
    }, [previousMapViewState, drawingControls, legendData, mapData?.features]); // Добавлена зависимость от mapData.features

    const handleImportConfirm = async (selectedFeatures) => {
        if (!selectedFeatures || selectedFeatures.length === 0) {
            toast.warn('Не выбрано ни одного объекта для импорта.');
            return;
        }

        // Генерируем новые ID для импортируемых объектов, чтобы избежать конфликтов
        const featuresWithNewIds = selectedFeatures.map(feature => ({
            ...feature,
            id: crypto.randomUUID(),
            properties: {
                ...feature.properties,
                id: undefined, // Удаляем старый ID из свойств, если он там был
            }
        }));

        try {
            const response = await fetch('/api/features/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ features: featuresWithNewIds })
            });

            if (!response.ok) {
                let errorMessage = `Ошибка на сервере: ${response.status} ${response.statusText}`;
                try {
                    // Попытка прочитать ошибку в формате JSON
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    // Если ответ не в формате JSON, просто используем текстовое сообщение
                }
                throw new Error(errorMessage);
            }

            await response.json(); // Получаем ответ от сервера
            toast.success(`Успешно импортировано ${featuresWithNewIds.length} объектов.`);
            
            // Перезагружаем все данные с сервера, чтобы получить актуальное состояние
            await fetchMetadata();

        } catch (error) {
            console.error('Ошибка при импорте:', error);
            toast.error(`Не удалось импортировать объекты: ${error.message}`);
        }
    };

    const handleFeatureUpdate = useCallback(async (updatedFeature) => {
        console.log('Updating geometry for feature:', updatedFeature);
        const originalFeature = featureBeforeEdit || mapData?.features?.find(f => f.id === updatedFeature.id);
        if (!originalFeature) return;

        let newAreaM2 = originalFeature.properties?.area;
        let newAreaSot = originalFeature.properties?.area_sot;
        try {
            if (['Polygon', 'MultiPolygon'].includes(updatedFeature.geometry?.type)) {
                newAreaM2 = Math.round(turfArea(updatedFeature) * 100) / 100;
                newAreaSot = Math.round((newAreaM2 / 100) * 100) / 100;
            }
        } catch (e) {}

        const featureWithNewGeometry = {
            ...originalFeature,
            geometry: updatedFeature.geometry,
            properties: {
                ...originalFeature.properties,
                ...(newAreaM2 !== undefined ? { area: newAreaM2, area_sot: newAreaSot } : {}),
                modified_by: 'geometry_edit',
                modified_at: new Date().toISOString()
            }
        };

        if (spdHistoryManager) {
            spdHistoryManager.pushAction({
                type: 'edit_geometry',
                description: `Изменение геометрии ${originalFeature.properties?.name || originalFeature.id}`,
                before: [
                    { layer: originalFeature.properties?.layerKey || 'plot', id: originalFeature.id, feature: originalFeature }
                ],
                after: [
                    { layer: originalFeature.properties?.layerKey || 'plot', id: originalFeature.id, feature: featureWithNewGeometry }
                ]
            });
        }

        try {
            const response = await fetch(`/api/feature/${featureWithNewGeometry.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(featureWithNewGeometry)
            });
            if (!response.ok) {
                await fetch(`/api/feature`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(featureWithNewGeometry)
                });
            }
        } catch (err) {
            console.error('Error saving updated geometry:', err);
        }

        await fetchMetadata();
        setFeatureToEditGeometry(null);
        setEditingFeature(featureWithNewGeometry);
        setFeatureBeforeEdit(null);
        if (drawingControls) {
            drawingControls.stopEditing();
        }
        toast.success(`Геометрия обновлена${newAreaM2 ? ` (${newAreaM2} м²)` : ''}`);
    }, [featureBeforeEdit, mapData, fetchMetadata, drawingControls]);

    // handleFeatureDelete не используется напрямую в MapViewer, но для консистентности
    const handleFeatureDelete = useCallback(() => console.log('Feature Deleted'), []); 

    const handleOverlayUpdate = useCallback((newOverlayData) => {
        console.log('Overlay Updated', newOverlayData);
    }, []);

    const selectedTopologyFeatures = useMemo(() => {
        const list = [];
        const seenIds = new Set();

        if (editingFeature && editingFeature.id) {
            seenIds.add(editingFeature.id);
            list.push(editingFeature);
        }

        if (Array.isArray(mapSelectedIds) && mapSelectedIds.length > 0) {
            mapSelectedIds.forEach(id => {
                if (!seenIds.has(id)) {
                    const found = mapData?.features?.find(f => f.id === id);
                    if (found) {
                        seenIds.add(found.id);
                        list.push(found);
                    }
                }
            });
        }

        return list;
    }, [mapData, mapSelectedIds, editingFeature]);

    const handleHistoryUndo = useCallback(async () => {
        if (!spdHistoryManager) return;
        const undoData = spdHistoryManager.undo();
        if (!undoData) return;

        try {
            if (Array.isArray(undoData.itemsToRestore)) {
                for (const item of undoData.itemsToRestore) {
                    if (item.feature && item.id) {
                        const res = await fetch(`/api/feature/${item.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(item.feature)
                        });
                        if (!res.ok) {
                            await fetch(`/api/feature`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(item.feature)
                            });
                        }
                    }
                }
            }

            if (Array.isArray(undoData.itemsToRemove)) {
                for (const item of undoData.itemsToRemove) {
                    if (item.id) {
                        await fetch(`/api/feature/${item.id}`, { method: 'DELETE' }).catch(() => {});
                    }
                }
            }

            await fetchMetadata();
            setEditingFeature(null);
            setMapSelectedIds([]);
            toast.info(`Действие отменено: ${undoData.description}`);
        } catch (err) {
            console.error('Undo error:', err);
            toast.error(`Ошибка при отмене действия: ${err.message}`);
        }
    }, [fetchMetadata]);

    const handleHistoryRedo = useCallback(async () => {
        if (!spdHistoryManager) return;
        const redoData = spdHistoryManager.redo();
        if (!redoData) return;

        try {
            const items = redoData.itemsToApply || redoData.itemsToRestore;

            if (Array.isArray(items)) {
                for (const item of items) {
                    if (item.isDeleted && item.id) {
                        await fetch(`/api/feature/${item.id}`, { method: 'DELETE' }).catch(() => {});
                    } else if (item.feature && item.id) {
                        const res = await fetch(`/api/feature/${item.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(item.feature)
                        });
                        if (!res.ok) {
                            await fetch(`/api/feature`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(item.feature)
                            });
                        }
                    }
                }
            }

            if (Array.isArray(redoData.itemsToRemove)) {
                for (const item of redoData.itemsToRemove) {
                    if (item.id) {
                        await fetch(`/api/feature/${item.id}`, { method: 'DELETE' }).catch(() => {});
                    }
                }
            }

            await fetchMetadata();
            setEditingFeature(null);
            setMapSelectedIds([]);
            toast.info(`Действие повторено: ${redoData.description}`);
        } catch (err) {
            console.error('Redo error:', err);
            toast.error(`Ошибка при повторе действия: ${err.message}`);
        }
    }, [fetchMetadata]);

    const executeDifference = useCallback(async (targetFeature, cutterFeature) => {
        if (!spdTopologyEngine || !targetFeature || !cutterFeature) return;
        try {
            const diffResult = spdTopologyEngine.performDifference(targetFeature, cutterFeature);

            if (spdHistoryManager) {
                spdHistoryManager.pushAction({
                    type: 'difference',
                    description: `Вырезание контура ${cutterFeature.properties?.name || cutterFeature.id} из ${targetFeature.properties?.name || targetFeature.id}`,
                    before: [
                        { layer: targetFeature.properties?.layerKey || 'plot', id: targetFeature.id, feature: targetFeature }
                    ],
                    after: [
                        { layer: targetFeature.properties?.layerKey || 'plot', id: targetFeature.id, feature: diffResult }
                    ]
                });
            }

            const response = await fetch(`/api/feature/${targetFeature.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(diffResult)
            });

            if (!response.ok) throw new Error('Ошибка сохранения результата вычитания на сервере');

            await fetchMetadata();
            setTopologyAction(null);
            toast.dismiss('topo-guide');
            setMapSelectedIds([]);
            setEditingFeature(diffResult);

            toast.success(`Контур успешно вырезан${diffResult.properties?.area ? ` (площадь: ${diffResult.properties.area} м²)` : ''}`);
        } catch (err) {
            console.error('Difference error:', err);
            toast.error(`Ошибка при вычитании: ${err.message}`);
        }
    }, [fetchMetadata]);

    const executeUnion = useCallback(async (featuresToUnion) => {
        if (!spdTopologyEngine || !featuresToUnion || featuresToUnion.length < 2) return;
        try {
            const masterFeature = featuresToUnion[0];
            const secondaryFeatures = featuresToUnion.slice(1);
            const unionResult = spdTopologyEngine.performUnion(featuresToUnion);

            if (spdHistoryManager) {
                spdHistoryManager.pushAction({
                    type: 'union',
                    description: `Объединение ${featuresToUnion.length} участков`,
                    before: featuresToUnion.map(f => ({
                        layer: f.properties?.layerKey || 'plot',
                        id: f.id,
                        feature: f
                    })),
                    after: [
                        { layer: masterFeature.properties?.layerKey || 'plot', id: masterFeature.id, feature: unionResult },
                        ...secondaryFeatures.map(f => ({
                            layer: f.properties?.layerKey || 'plot',
                            id: f.id,
                            isDeleted: true
                        }))
                    ]
                });
            }

            const putRes = await fetch(`/api/feature/${masterFeature.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(unionResult)
            });
            if (!putRes.ok) throw new Error('Не удалось обновить объединенный объект');

            for (const sec of secondaryFeatures) {
                await fetch(`/api/feature/${sec.id}`, { method: 'DELETE' });
            }

            await fetchMetadata();
            setMapSelectedIds([masterFeature.id]);
            setEditingFeature(unionResult);
            setTopologyAction(null);
            toast.dismiss('topo-guide');

            toast.success(`Объединено ${featuresToUnion.length} участков`);
        } catch (err) {
            console.error('Union error:', err);
            toast.error(`Ошибка при объединении: ${err.message}`);
        }
    }, [fetchMetadata]);

    const handleTopologyDifference = useCallback(() => {
        if (topologyAction === 'difference') {
            setTopologyAction(null);
            toast.dismiss('topo-guide');
            return;
        }

        if (selectedTopologyFeatures.length >= 2) {
            // Первый выбранный участок вырезается из второго
            executeDifference(selectedTopologyFeatures[1], selectedTopologyFeatures[0]);
        } else if (editingFeature) {
            setTopologyAction('difference');
            toast.info('Кликните на второй участок на карте, из которого нужно вырезать этот контур (Esc для отмены)', {
                toastId: 'topo-guide',
                autoClose: 15000
            });
        }
    }, [topologyAction, selectedTopologyFeatures, editingFeature, executeDifference]);

    const handleTopologyUnion = useCallback(() => {
        if (topologyAction === 'union') {
            setTopologyAction(null);
            toast.dismiss('topo-guide');
            return;
        }

        if (selectedTopologyFeatures.length >= 2) {
            executeUnion(selectedTopologyFeatures);
        } else if (editingFeature) {
            setTopologyAction('union');
            toast.info('Кликните на смежный участок на карте для объединения (Esc для отмены)', {
                toastId: 'topo-guide',
                autoClose: 15000
            });
        }
    }, [topologyAction, selectedTopologyFeatures, editingFeature, executeUnion]);

    const handleFeatureSelectForEdit = useCallback((feature) => {
        let featureId = feature.id || feature.properties?.id;

        if (!featureId) {
            featureId = crypto.randomUUID();
        }

        const originalFeature = mapData?.features?.find(f => f.id === featureId);
        const geometry = originalFeature ? originalFeature.geometry : feature.geometry;

        const cleanFeature = {
            type: 'Feature',
            geometry: geometry,
            properties: { ...feature.properties, id: featureId },
            id: featureId
        };

        // Если активен режим интерактивной топологии
        if (topologyAction && editingFeature) {
            if (cleanFeature.id === editingFeature.id) {
                toast.warn('Выбран тот же объект. Кликните на другой участок на карте.', { toastId: 'topo-warn' });
                return;
            }
            if (topologyAction === 'difference') {
                // Первый участок (editingFeature) вырезается из второго (cleanFeature)
                executeDifference(cleanFeature, editingFeature);
                return;
            } else if (topologyAction === 'union') {
                executeUnion([editingFeature, cleanFeature]);
                return;
            }
        }

        // Сохраняем вид карты, только если панель редактирования была закрыта (т.е. editingFeature был null)
        if (!editingFeature) {
            setPreviousMapViewState(currentMapViewStateRef.current);
        }
        setEditingFeature(cleanFeature);
    }, [editingFeature, mapData, topologyAction, executeDifference, executeUnion]);

    // Выбор объекта из менеджера объектов
    const handleFeatureSelectFromManager = useCallback((feature) => {
        // Открываем панель редактирования
        handleFeatureSelectForEdit(feature);
        // Центрируемся на объекте
        const map = mapRef.current?.getMap();
        if (map && feature.geometry) {
            const featureBbox = bbox(feature);
            map.fitBounds(featureBbox, {
                padding: { top: 100, bottom: 100, left: 400, right: 400 }, // Отступы для панелей
                duration: 1000
            });
        }
    }, [handleFeatureSelectForEdit]);

    // Переключение видимости объекта из менеджера
    const handleFeatureVisibilityChange = useCallback((featureId, isVisible) => {
        const visibilities = getSavedVisibilities();
        visibilities[featureId] = isVisible;
        localStorage.setItem('featureVisibilities', JSON.stringify(visibilities));

        setMapData(prevData => {
            if (!prevData || !prevData.features) return prevData;

            const updatedFeatures = prevData.features.map(f => {
                if (f.id === featureId) {
                    // Создаем новый объект properties, чтобы избежать мутации
                    const newProperties = { ...f.properties, visible: isVisible };
                    return { ...f, properties: newProperties };
                }
                return f;
            });

            return {
                ...prevData,
                features: updatedFeatures,
            };
        });
    }, [getSavedVisibilities]);

    // Массовое переключение видимости объектов из менеджера
    const handleBulkVisibilityChange = useCallback((featureIds, isVisible) => {
        const visibilities = getSavedVisibilities();
        featureIds.forEach(id => {
            visibilities[id] = isVisible;
        });
        localStorage.setItem('featureVisibilities', JSON.stringify(visibilities));

        setMapData(prevData => {
            if (!prevData || !prevData.features) return prevData;

            const idSet = new Set(featureIds);
            const updatedFeatures = prevData.features.map(f => {
                if (idSet.has(f.id)) {
                    return { ...f, properties: { ...f.properties, visible: isVisible } };
                }
                return f;
            });

            return { ...prevData, features: updatedFeatures };
        });
        toast.info(`Видимость ${featureIds.length} объектов обновлена`);
    }, [getSavedVisibilities]);

    // Функция для закрытия всех панелей
    const closeAllPanels = useCallback(() => {
        console.log('Closing all panels / Cancelling edit');
        // Если мы отменяем редактирование геометрии, восстанавливаем исходный объект на карте
        if (featureBeforeEdit) {
            setMapData(prevData => {
                const newFeatures = [...prevData.features, featureBeforeEdit];
                return { ...prevData, features: newFeatures };
            });
        }

        if (drawingControls) {
            drawingControls.clearDrawing();
        }
        setEditingFeature(currentFeature => {
            if (currentFeature && !currentFeature.properties.id) setFeatureToCancel(currentFeature.id);
            return null;
        });
        setFeatureBeforeEdit(null);
        setFeatureToEditGeometry(null);
        setTopologyAction(null);
        setDuplicatingFeature(null);
        setMapSelectedIds([]);
        toast.dismiss('topo-guide');
        setRestoreMapViewState(previousMapViewState); // Запускаем восстановление вида карты
    }, [drawingControls, previousMapViewState]);

    const handleDeleteFeature = useCallback(() => {
        if (!editingFeature) return;
        setDeleteConfirmTarget(editingFeature);
    }, [editingFeature]);

    const handleConfirmDeleteFeature = useCallback(async () => {
        if (!deleteConfirmTarget) return;

        if (!deleteConfirmTarget.id) {
            toast.warn('Нельзя удалить несохраненный объект.');
            setDeleteConfirmTarget(null);
            return;
        }

        setIsDeletingFeature(true);
        try {
            const response = await fetch(`/api/feature/${deleteConfirmTarget.id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error('Ошибка удаления');
            await response.json();
            toast.success('Объект успешно удален');
            setDeleteConfirmTarget(null);
            closeAllPanels();
            await fetchMetadata();
        } catch (error) {
            console.error('Ошибка при удалении:', error);
            toast.error('Не удалось удалить объект');
        } finally {
            setIsDeletingFeature(false);
        }
    }, [deleteConfirmTarget, closeAllPanels, fetchMetadata]);

    const handleEditGeometry = useCallback(() => {
        if (!editingFeature) return;
        console.log('Start editing geometry for:', editingFeature.id);
        setFeatureBeforeEdit(editingFeature); // Сохраняем исходное состояние
        setFeatureToEditGeometry(editingFeature);
        setEditingFeature(null); // Закрываем панель свойств
    }, [editingFeature]);

    const handleCancelGeometryEdit = useCallback(() => {
        if (featureToEditGeometry) {
            const restored = featureBeforeEdit || featureToEditGeometry;
            setFeatureToEditGeometry(null);
            setEditingFeature(restored);
            setFeatureBeforeEdit(null);
            if (drawingControls) {
                drawingControls.stopEditing();
            }
            toast.info('Редактирование геометрии отменено');
        }
    }, [featureToEditGeometry, featureBeforeEdit, drawingControls]);

    const handleStartDuplicate = useCallback((feature) => {
        const target = feature || editingFeature;
        if (!target) return;
        setEditingFeature(null);
        setFeatureBeforeEdit(null);
        setFeatureToEditGeometry(null);
        setTopologyAction(null);
        setDuplicatingFeature(target);
    }, [editingFeature]);

    const handleDuplicatePlaced = useCallback((newFeature) => {
        if (!newFeature) return;
        setDuplicatingFeature(null);
        setPreviousMapViewState(currentMapViewStateRef.current);
        setEditingFeature(newFeature);
        toast.success('Копия размещена! Проверьте параметры и нажмите «Сохранить»');
    }, []);

    const handleDuplicateCancelled = useCallback(() => {
        setDuplicatingFeature(null);
        toast.info('Дублирование отменено');
    }, []);

    const isDrawingTool = activeMode === 'drawing' && activeTool && (
        activeTool === 'draw-plot' ||
        activeTool === 'draw-building' ||
        activeTool === 'draw-line' ||
        activeTool === 'draw-road' ||
        activeTool === 'draw-marker' ||
        activeTool.startsWith('draw-')
    );

    const drawingToolInfo = useMemo(() => {
        if (!activeTool) return null;
        if (activeTool === 'draw-plot') {
            return { title: 'Рисование участка', icon: <DrawPolygonIcon size={15} /> };
        }
        if (activeTool === 'draw-building') {
            return { title: 'Рисование здания', icon: <BuildingIcon size={15} /> };
        }
        if (activeTool === 'draw-line' || activeTool === 'draw-road') {
            return { title: 'Трассировка линии', icon: <DrawLineIcon size={15} /> };
        }
        if (activeTool === 'draw-marker') {
            return { title: 'Установка метки', icon: <PinIcon size={15} /> };
        }
        return { title: 'Рисование объекта', icon: <DrawPolygonIcon size={15} /> };
    }, [activeTool]);

    // Эффект для отмены редактирования по клавише Escape
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (featureToEditGeometry) {
                    handleCancelGeometryEdit();
                    return;
                }
                if (isDrawingTool) {
                    if (drawingControls?.clearDrawing) {
                        drawingControls.clearDrawing();
                    }
                    handleToolChange(null);
                    return;
                }
                closeAllPanels();
            }
        };

        if (editingFeature || featureToEditGeometry || isDrawingTool) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [editingFeature, featureToEditGeometry, isDrawingTool, drawingControls, closeAllPanels, handleCancelGeometryEdit, handleToolChange]);

    // Горячие клавиши Ctrl+Z / Ctrl+Y / Backspace
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
            if (isTyping) return;

            if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyZ' || e.key.toLowerCase() === 'z' || e.key === 'я')) {
                e.preventDefault();
                if (featureToEditGeometry && drawingControls) {
                    if (e.shiftKey) {
                        drawingControls.redoVertex();
                    } else {
                        drawingControls.undoVertex();
                    }
                    return;
                }
                if (isDrawingTool && drawingControls) {
                    drawingControls.undoLastPoint();
                    return;
                }
                if (isSPDPlusActive) {
                    if (e.shiftKey) {
                        handleHistoryRedo();
                    } else {
                        handleHistoryUndo();
                    }
                }
            } else if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyY' || e.key.toLowerCase() === 'y' || e.key === 'н')) {
                e.preventDefault();
                if (featureToEditGeometry && drawingControls) {
                    drawingControls.redoVertex();
                    return;
                }
                if (isSPDPlusActive) {
                    handleHistoryRedo();
                }
            } else if (e.key === 'Backspace' || e.key === 'Delete') {
                if (isDrawingTool && drawingControls) {
                    e.preventDefault();
                    drawingControls.undoLastPoint();
                    return;
                }
            }

            // Одиночные горячие клавиши быстрого доступа (когда не зажаты модификаторы и нет активного чертежа)
            if (!e.ctrlKey && !e.altKey && !e.metaKey && !featureToEditGeometry && !isDrawingTool && !document.activeElement?.isContentEditable) {
                const key = e.key.toLowerCase();
                const code = e.code;

                // 1, 2, 3: Переключение режимов работы
                if (key === '1' || code === 'Digit1' || code === 'Numpad1') {
                    e.preventDefault();
                    setActiveMode('drawing');
                    setActiveTool(null);
                    return;
                }
                if (key === '2' || code === 'Digit2' || code === 'Numpad2') {
                    e.preventDefault();
                    setActiveMode('data');
                    setActiveTool(null);
                    return;
                }
                if (key === '3' || code === 'Digit3' || code === 'Numpad3') {
                    e.preventDefault();
                    if (modes.includes('printing')) {
                        setActiveMode('printing');
                        setActiveTool(null);
                    }
                    return;
                }

                // РЯД 1 (Q, W, E, R, T) — Инструменты рисования / черчения
                // Q / Й - Участки
                if (key === 'q' || key === 'й' || code === 'KeyQ') {
                    e.preventDefault();
                    if (activeMode !== 'drawing') setActiveMode('drawing');
                    handleToolChange('draw-plot');
                    return;
                }
                // W / Ц - Здания
                if (key === 'w' || key === 'ц' || code === 'KeyW') {
                    e.preventDefault();
                    if (activeMode !== 'drawing') setActiveMode('drawing');
                    handleToolChange('draw-building');
                    return;
                }
                // E / У - Линии / Дороги
                if (key === 'e' || key === 'у' || code === 'KeyE') {
                    e.preventDefault();
                    if (activeMode !== 'drawing') setActiveMode('drawing');
                    handleToolChange('draw-line');
                    return;
                }
                // R / К - Метки
                if (key === 'r' || key === 'к' || code === 'KeyR') {
                    e.preventDefault();
                    if (activeMode !== 'drawing') setActiveMode('drawing');
                    handleToolChange('draw-marker');
                    return;
                }
                // T / Е - Растровая подложка
                if (key === 't' || key === 'е' || code === 'KeyT') {
                    e.preventDefault();
                    if (activeMode !== 'drawing') setActiveMode('drawing');
                    handleToolChange('basemap');
                    return;
                }

                // РЯД 2 (A, S, D, F, G) — Инструменты режима «Данные» и измерений
                // A / Ф - Линейка (расстояние)
                if (key === 'a' || key === 'ф' || code === 'KeyA') {
                    e.preventDefault();
                    if (activeMode !== 'data') setActiveMode('data');
                    handleToolChange('measure-ruler');
                    return;
                }
                // S / Ы - Площадь
                if (key === 's' || key === 'ы' || code === 'KeyS') {
                    e.preventDefault();
                    if (activeMode !== 'data') setActiveMode('data');
                    handleToolChange('measure-area');
                    return;
                }
                // D / В - Координаты точки
                if (key === 'd' || key === 'в' || code === 'KeyD') {
                    e.preventDefault();
                    if (activeMode !== 'data') setActiveMode('data');
                    handleToolChange('measure-coords');
                    return;
                }
                // F / А - Менеджер объектов
                if (key === 'f' || key === 'а' || code === 'KeyF') {
                    e.preventDefault();
                    setObjectManagerVisible(prev => !prev);
                    return;
                }
                // G / П - Легенда категорий
                if (key === 'g' || key === 'п' || code === 'KeyG') {
                    e.preventDefault();
                    setLegendManagerVisible(prev => !prev);
                    return;
                }

                // РЯД 3 (Z, X, C, V) — Действия
                // Z / Я - Отмена последнего действия (история / топология)
                if (key === 'z' || key === 'я' || code === 'KeyZ') {
                    e.preventDefault();
                    if (isSPDPlusActive) {
                        handleHistoryUndo();
                    }
                    return;
                }
                // X / Ч - Сброс активного инструмента (режим выбора)
                if (key === 'x' || key === 'ч' || code === 'KeyX') {
                    e.preventDefault();
                    setActiveTool(null);
                    return;
                }
                // C / С - Повтор действия (Redo)
                if (key === 'c' || key === 'с' || code === 'KeyC') {
                    e.preventDefault();
                    if (isSPDPlusActive) {
                        handleHistoryRedo();
                    }
                    return;
                }
                // V / М - Экспорт данных
                if (key === 'v' || key === 'м' || code === 'KeyV') {
                    e.preventDefault();
                    handleExport();
                    return;
                }

                // ? или F1 - Справка / Документация
                if (key === '?' || code === 'F1') {
                    e.preventDefault();
                    window.open('/map/admin/documentation', '_blank');
                    return;
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isSPDPlusActive, featureToEditGeometry, isDrawingTool, drawingControls, handleHistoryUndo, handleHistoryRedo, activeMode, modes, handleToolChange]);

    // Расчет групп дубликатов SPD+
    const duplicateGroups = useMemo(() => {
        if (!spdPlusModule?.duplicateDetector || !mapData?.features) return [];
        const detector = spdPlusModule.duplicateDetector;
        if (typeof detector.findDuplicatesFromFeatures === 'function' && typeof detector.groupDuplicates === 'function') {
            const dups = detector.findDuplicatesFromFeatures(mapData.features, { minOverlapPercent: 80, checkCrossLayers: true });
            return detector.groupDuplicates(dups);
        }
        return [];
    }, [mapData]);

    // Данные для карты: в режиме дубликатов отображаются только дублирующиеся объекты с цветами групп
    const displayMapData = useMemo(() => {
        if (!isDuplicateModeActive) {
            return mapData;
        }
        if (!duplicateGroups || duplicateGroups.length === 0) {
            return {
                type: 'FeatureCollection',
                features: []
            };
        }
        const duplicateFeatures = [];
        duplicateGroups.forEach(group => {
            group.features.forEach(feat => {
                const orig = mapData?.features?.find(f => f.id === feat.id) || feat;
                duplicateFeatures.push({
                    ...orig,
                    properties: {
                        ...(orig.properties || {}),
                        color: group.color,
                        _origColor: orig.properties?.color,
                        _isDuplicate: true,
                        _duplicateGroupId: group.id,
                        visible: true
                    }
                });
            });
        });
        return {
            type: 'FeatureCollection',
            features: duplicateFeatures
        };
    }, [isDuplicateModeActive, mapData, duplicateGroups]);

    // Детальная информация о дубликате для выбранного объекта
    const currentFeatureDuplicateInfo = useMemo(() => {
        if (!isDuplicateModeActive || !editingFeature?.id || !spdPlusModule?.duplicateDetector) return null;
        const detector = spdPlusModule.duplicateDetector;
        if (typeof detector.getFeatureDuplicateInfo === 'function') {
            return detector.getFeatureDuplicateInfo(editingFeature.id, duplicateGroups);
        }
        return null;
    }, [isDuplicateModeActive, editingFeature, duplicateGroups]);


    // Обработчик для инструмента "Координаты"
    const handleCoordsSelect = useCallback((info) => {
        setCoordsInfo(info);
        // Фокусируемся на точке с отступом справа для панели
        const map = mapRef.current?.getMap();
        if (map) {
            map.easeTo({
                center: info.coordinates,
                zoom: Math.max(map.getZoom(), 17), // Приближаемся, если карта далеко
                padding: { right: 380 }, // Отступ для панели (360px + 20px)
                duration: 1000
            });
        }
    }, []);

    const handleCoordsFeatureSelect = useCallback((feature) => {
        setCoordsInfo(null); // Закрываем панель координат
        setActiveTool(null); // Деактивируем инструмент "Координаты"
        handleFeatureSelectForEdit(feature); // Открываем панель редактирования для выбранного объекта
    }, [handleFeatureSelectForEdit]);

    // Обработчик для инструмента "НСПД"
    const handleNSPDSelect = useCallback((info) => {
        setNspdInfo(info);
        setNSPDModalVisible(true); // Открываем модальное окно при клике на карту
        setActiveTool(null); // Деактивируем инструмент после клика
    }, []);

    const handleBulkUpdate = useCallback(async (updatedFeatures) => {
        try {
            const response = await fetch('/api/features/bulk-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ features: updatedFeatures })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка массового обновления');
            }

            toast.success(`Успешно обновлено ${updatedFeatures.length} объектов`);
            await fetchMetadata(); // Перезагружаем данные
        } catch (error) {
            console.error('Bulk update error:', error);
            toast.error(`Не удалось обновить объекты: ${error.message}`);
        }
    }, [fetchMetadata, legendData]);

    const handleShiftSelect = useCallback((featureId) => {
        setMapSelectedIds(prev => {
            if (prev.includes(featureId)) {
                return prev.filter(id => id !== featureId);
            } else {
                return [...prev, featureId];
            }
        });
    }, []);

    // Эффект для отслеживания отпускания Shift
    useEffect(() => {
        const handleKeyUp = (e) => {
            if (e.key === 'Shift') {
                setMapSelectedIds(ids => {
                    if (ids.length > 0) {
                        // Если есть выделенные объекты, открываем менеджер объектов
                        setObjectManagerVisible(true);
                        // Выбранные ID передадутся в ObjectManagerPanel через проп initialSelectedIds (нужно добавить)
                    }
                    return ids; // Оставляем их выделенными, чтобы передать в менеджер
                });
            }
        };

        window.addEventListener('keyup', handleKeyUp);
        return () => window.removeEventListener('keyup', handleKeyUp);
    }, []);

    // Сброс выделения при закрытии менеджера объектов
    useEffect(() => {
        if (!isObjectManagerVisible) {
            setMapSelectedIds([]);
        }
    }, [isObjectManagerVisible]);

    const handleBulkDelete = useCallback(async (featureIds) => {
        try {
            const response = await fetch('/api/features/bulk-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: featureIds })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Ошибка массового удаления');
            }

            toast.success(`Успешно удалено ${featureIds.length} объектов`);
            await fetchMetadata(); // Перезагружаем данные
        } catch (error) {
            console.error('Bulk delete error:', error);
            toast.error(`Не удалось удалить объекты: ${error.message}`);
        }
    }, [fetchMetadata, legendData]);

    // Стабилизируем объект с опциями для инструментов
    const toolOptions = useMemo(() => ({
        'measure-coords': { onCoordsSelect: handleCoordsSelect },
        'nspd-search': { onNSPDSelect: handleNSPDSelect }
    }), [handleCoordsSelect, handleNSPDSelect]); // Зависимость корректна

    // Стабилизируем фильтры по умолчанию, чтобы не создавать новый объект при каждом рендере
    const defaultActiveFilters = useMemo(() => {
        // В админ панели по умолчанию показываем все категории (берём из legendData)
        const allPlotIds = (legendData.plots || []).map(p => p.id);
        const allBuildingIds = (legendData.buildings || []).map(b => b.id);
        const allRoadIds = (legendData.roads || []).map(r => r.id);
        const allMarkerIds = (legendData.markers || []).map(m => m.id);
        
        return {
            plots: allPlotIds,
            buildings: allBuildingIds,
            roads: allRoadIds,
            markers: allMarkerIds,
            status: ['for_sale', 'is_building', 'is_planned', 'no_status']
        };
    }, [legendData]);

    return (
        <div className={styles.adminDashboardPage}>
            {/* Header */}
            <Header
                title="IChis SPD"
                subtitle={`v${packageInfo.version || '1.2.1'}`}
                navItems={navItems}
                toolbarTools={toolsConfig[activeMode]}
                activeTool={activeTool}
                onToolChange={handleToolChange}
                actions={headerActions}
                user={{ name: 'Администратор', avatar: null }}
                sticky
            />

            <input
                type="file"
                ref={importInputRef}
                style={{ display: 'none' }}
                accept=".json,application/json"
                onChange={handleFileImport}
            />

            <div className={styles.mainContainer}>
                {/* Основная область */}
                <main className={styles.dashboardContainer}>
                    {A4PrintLayout ? (
                        <A4PrintLayout 
                            isPrintMode={activeMode === 'printing'}
                            mapData={displayMapData} 
                            legendData={legendData}
                            title={document.title || 'Схема генерального плана'}
                        >
                            <MapViewer
                                printingSettings={printingSettings}
                                mapData={displayMapData}
                                styleSwapControlProps={{
                                    onClick: handleMapStyleChange,
                                    title: `Стиль: ${mapStyleNames[mapStyleId]}`,
                                    iconSvg: mapStyleIcons[mapStyleId]
                                }}
                                mapStyleId={mapStyleId}
                                activeTool={activeTool}
                                toolOptions={toolOptions}
                                onFeatureUpdate={handleFeatureUpdate}
                                onFeatureDelete={handleFeatureDelete}
                                onFeatureCreate={handleFeatureCreate}
                                onFeatureSelect={handleFeatureSelectForEdit}
                                featureToEditGeometry={featureToEditGeometry}
                                overlay={initialOverlay}
                                featureBeforeEdit={featureBeforeEdit}
                                mapRef={mapRef}
                                onOverlayUpdate={handleOverlayUpdate}
                                focusedFeature={editingFeature}
                                featureToCancel={featureToCancel}
                                onCancelComplete={handleCancelComplete}
                                onMapMove={setCurrentMapViewState}
                                mapSelectedIds={mapSelectedIds}
                                onShiftSelect={handleShiftSelect}
                                isShiftSelectionEnabled={isSPDPlusActive}
                                restoreMapViewState={restoreMapViewState}
                                onRestoreComplete={() => setRestoreMapViewState(null)}
                                legendData={legendData}
                                activeFilters={defaultActiveFilters}
                                onDrawingControlsReady={setDrawingControls}
                                onFeatureCancelEdit={handleCancelGeometryEdit}
                                onToolDeactivateRequest={() => setActiveTool(null)}
                                duplicatingFeature={duplicatingFeature}
                                onDuplicatePlaced={handleDuplicatePlaced}
                                onDuplicateCancelled={handleDuplicateCancelled}
                            />
                        </A4PrintLayout>
                    ) : (
                        <MapViewer
                            printingSettings={(activeMode === 'printing' || activeMode === 'export') ? printingSettings : null}
                            mapData={displayMapData}
                            styleSwapControlProps={{
                                onClick: handleMapStyleChange,
                                title: `Стиль: ${mapStyleNames[mapStyleId]}`,
                                iconSvg: mapStyleIcons[mapStyleId]
                            }}
                            mapStyleId={mapStyleId}
                            activeTool={activeTool}
                            toolOptions={toolOptions}
                            // Колбэки для обновления данных
                            onFeatureUpdate={handleFeatureUpdate}
                            onFeatureDelete={handleFeatureDelete}
                            onFeatureCreate={handleFeatureCreate}
                            onFeatureSelect={handleFeatureSelectForEdit}
                            featureToEditGeometry={featureToEditGeometry}
                            overlay={initialOverlay}
                            featureBeforeEdit={featureBeforeEdit}
                            mapRef={mapRef}
                            onOverlayUpdate={handleOverlayUpdate}
                            focusedFeature={editingFeature}
                            featureToCancel={featureToCancel}
                            onCancelComplete={handleCancelComplete}
                            onMapMove={setCurrentMapViewState} // Передаем колбэк для обновления текущего состояния карты
                            mapSelectedIds={mapSelectedIds}
                            onShiftSelect={handleShiftSelect}
                            isShiftSelectionEnabled={isSPDPlusActive}
                            restoreMapViewState={restoreMapViewState} // Передаем состояние для восстановления
                            onRestoreComplete={() => setRestoreMapViewState(null)} // Колбэк для сброса состояния восстановления
                            legendData={legendData}
                            activeFilters={defaultActiveFilters}
                            onDrawingControlsReady={setDrawingControls}
                            onFeatureCancelEdit={handleCancelGeometryEdit}
                            onToolDeactivateRequest={() => setActiveTool(null)}
                            duplicatingFeature={duplicatingFeature}
                            onDuplicatePlaced={handleDuplicatePlaced}
                            onDuplicateCancelled={handleDuplicateCancelled}
                        />
                    )}
                </main>
            </div>
             {/* Панель редактирования теперь рендерится всегда, а видимость управляется внутри нее */}

             {/* Плавающая панель при редактировании существующей геометрии */}
             {featureToEditGeometry && (
                 <div className={styles.geomEditBar}>
                     <div className={styles.geomEditBadge}>
                         <span className={styles.geomEditBadgeIcon}><EditIcon size={15} /></span>
                         <span>Правка границ</span>
                     </div>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnIconOnly}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.undoVertex();
                         }}
                         title="Отменить шаг (Ctrl+Z)"
                     >
                         ↶
                     </button>
                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnIconOnly}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.redoVertex();
                         }}
                         title="Повторить шаг (Ctrl+Y)"
                     >
                         ↷
                     </button>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnRedraw}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             if (drawingControls?.clearAndRedraw) {
                                 drawingControls.clearAndRedraw();
                                 toast.info('Точки очищены. Нарисуйте новый контур на карте кликами мыши.', { toastId: 'geom-clear-info' });
                             }
                         }}
                         title="Очистить точки и нарисовать контур с нуля"
                     >
                         <TrashIcon size={14} />
                         <span>С нуля</span>
                     </button>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnSave}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.saveEditing();
                         }}
                         title="Сохранить геометрию (Enter)"
                     >
                         <SaveIcon size={14} />
                         <span>Сохранить</span>
                     </button>
                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnCancel}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             handleCancelGeometryEdit();
                         }}
                         title="Отменить изменения (Esc)"
                     >
                         <CloseIcon size={14} />
                         <span>Отмена</span>
                     </button>
                 </div>
             )}

             {/* Плавающая панель при рисовании новых объектов (участки, здания, линии) */}
             {!featureToEditGeometry && isDrawingTool && drawingToolInfo && (
                 <div className={styles.geomEditBar}>
                     <div className={styles.geomEditBadge}>
                         <span className={styles.geomEditBadgeIcon}>{drawingToolInfo.icon}</span>
                         <span>{drawingToolInfo.title}</span>
                     </div>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnIconOnly}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.undoLastPoint();
                         }}
                         title="Отменить последнюю точку (Ctrl+Z / Backspace)"
                     >
                         ↶
                     </button>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnRedraw}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.resetDrawing();
                             toast.info('Точки сброшены. Начните рисовать заново.', { toastId: 'draw-reset-info' });
                         }}
                         title="Сбросить точки и начать заново"
                     >
                         <TrashIcon size={14} />
                         <span>С нуля</span>
                     </button>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnSave}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             drawingControls?.completeDrawing();
                         }}
                         title="Завершить рисование (Enter / двойной клик)"
                     >
                         <SaveIcon size={14} />
                         <span>Завершить</span>
                     </button>
                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnCancel}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             if (drawingControls?.clearDrawing) {
                                 drawingControls.clearDrawing();
                             }
                             setActiveTool(null);
                         }}
                         title="Отменить рисование и выйти (Esc)"
                     >
                         <CloseIcon size={14} />
                         <span>Отмена</span>
                     </button>
                 </div>
             )}

             {/* Плавающая панель при дублировании объекта (1 в 1 как при рисовании) */}
             {duplicatingFeature && (
                 <div className={styles.geomEditBar}>
                     <div className={styles.geomEditBadge}>
                         <span className={styles.geomEditBadgeIcon}><CopyIcon size={15} /></span>
                         <span>Дублирование: {duplicatingFeature.properties?.name || 'Объект'}</span>
                     </div>

                     <div className={styles.geomEditDivider} />

                     <div className={styles.geomEditBadge} style={{ fontSize: '12px' }}>
                         <span>Зажмите <strong>Shift</strong> для стыковки по 4 сторонам</span>
                     </div>

                     <div className={styles.geomEditDivider} />

                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnSave}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             if (drawingControls?.placeDuplicate) {
                                 drawingControls.placeDuplicate();
                             }
                         }}
                         title="Разместить объект на карте (клик / Enter)"
                     >
                         <SaveIcon size={14} />
                         <span>Разместить</span>
                     </button>
                     <button
                         type="button"
                         className={`${styles.geomEditBtn} ${styles.geomEditBtnCancel}`}
                         onClick={(e) => {
                             e.stopPropagation();
                             handleDuplicateCancelled();
                         }}
                         title="Отменить дублирование (Esc)"
                     >
                         <CloseIcon size={14} />
                         <span>Отмена</span>
                     </button>
                 </div>
             )}

              <ActionToolbar
                  isVisible={!!editingFeature && !featureToEditGeometry && !activeTool}
                  onEditGeometry={handleEditGeometry}
                  onDelete={handleDeleteFeature}
                 onNSPDUpdate={() => setNSPDUpdateModalOpen(true)}
                 hasCadastralNumber={!!editingFeature?.properties?.cadastral_number}
                 hasNSPD={!!nspdModule}
                 extraActions={(isSPDPlusActive && !!editingFeature && !featureToEditGeometry && !activeTool) ? (
                     <TopologyToolbar
                         selectedFeatures={selectedTopologyFeatures}
                         hasActiveFeature={Boolean(editingFeature)}
                         activeAction={topologyAction}
                         onDifference={handleTopologyDifference}
                         onUnion={handleTopologyUnion}
                         onDuplicate={() => handleStartDuplicate(editingFeature)}
                         onUndo={handleHistoryUndo}
                         onRedo={handleHistoryRedo}
                     />
                 ) : null}
             />

             <NSPDUpdateModal 
                 isVisible={isNSPDUpdateModalOpen}
                 onClose={() => setNSPDUpdateModalOpen(false)}
                 feature={editingFeature}
                 onUpdate={(updatedFeature) => {
                     handleEditingSave(updatedFeature);
                     setEditingFeature(updatedFeature);
                 }}
             />

             <EditingPanel
                 feature={editingFeature}
                 onCancel={closeAllPanels}
                 onSave={handleEditingSave}
                 availableProperties={featureProperties}
                 onLegendUpdate={fetchMetadata}
                 legendData={legendData}
                 mapData={mapData}
                 duplicateInfo={currentFeatureDuplicateInfo}
             />
             {console.log('AdminDashboardPage: Rendering Page. featuresCount:', mapData?.features?.length || 0, 'legendLoaded:', !!legendData, 'mode:', activeMode)}

             <LegendManager
                 isVisible={isLegendManagerVisible}
                 onClose={() => setLegendManagerVisible(false)}
                 onSave={fetchMetadata} // Перезагружаем все метаданные после сохранения
             />

             {/* Панели инструментов из динамических модулей */}
             {modules.map(mod => {
                 console.log(`Checking module: ${mod.id}`, { hasPanel: !!mod.Panel, activeMode });
                 // Определяем, относится ли текущий режим к этому модулю
                 const modTools = mod.getTools ? mod.getTools(iconComponents) : {};
                 const isModeHandledByModule = activeMode === mod.id || Object.keys(modTools).includes(activeMode);
                 const isNSPDModal = mod.id === 'nspd-integration' && isNSPDModalVisible;

                 console.log(`Module ${mod.id} condition:`, { isModeHandledByModule, isNSPDModal });

                 if (mod.Panel && (isModeHandledByModule || isNSPDModal)) {
                     const ModulePanel = mod.Panel;
                     return (
                         <ModulePanel 
                             key={mod.id}
                             isVisible={mod.id === 'nspd-integration' ? isNSPDModalVisible : true}
                             onClose={() => mod.id === 'nspd-integration' ? setNSPDModalVisible(false) : setActiveMode('drawing')}
                             mapData={mapData}
                             legendData={legendData}
                             activeTool={activeTool}
                             setActiveTool={setActiveTool}
                             nspdInfo={mod.id === 'nspd-integration' ? nspdInfo : null}
                             onMapSelectRequest={() => {
                                 setNSPDModalVisible(false);
                                 setActiveTool('nspd-search');
                             }}
                             activeFilters={defaultActiveFilters}
                             onImport={fetchMetadata}
                             onFeatureSelect={handleFeatureSelectFromManager}
                             mapRef={mapRef}
                         />
                     );
                 }
                 return null;
             })}

             <ObjectManagerPanel
                 isVisible={isObjectManagerVisible}
                 onClose={() => setObjectManagerVisible(false)}
                 mapData={mapData}
                 onFeatureSelect={handleFeatureSelectFromManager}
                 focusedFeatureId={editingFeature?.id}
                 onVisibilityChange={handleFeatureVisibilityChange}
                 onBulkVisibilityChange={handleBulkVisibilityChange}
                 onBulkUpdate={handleBulkUpdate}
                 onBulkDelete={handleBulkDelete}
                 initialSelectedIds={mapSelectedIds}
                 onSelectionChange={setMapSelectedIds}
                 isBulkEditEnabled={isSPDPlusActive}
                 isDuplicateModeActive={isDuplicateModeActive}
                 onToggleDuplicateMode={setIsDuplicateModeActive}
                 duplicateGroups={duplicateGroups}
                 legendData={legendData}
             />

            <CoordsInfoPanel
                info={coordsInfo}
                onClose={() => {
                    setCoordsInfo(null);
                    // Если инструмент "Координаты" все еще активен, деактивируем его
                    if (activeTool === 'measure-coords') {
                        setActiveTool(null);
                    }
                }}
                onFeatureClick={handleCoordsFeatureSelect}
            />

            <ImportModal
                isVisible={isImportModalVisible}
                features={featuresToImport}
                onClose={() => {
                    setImportModalVisible(false);
                    setFeaturesToImport([]);
                }}
                onImport={handleImportConfirm}
            />

            {/* Модальное окно подтверждения удаления объекта */}
            <ConfirmDialog
                show={!!deleteConfirmTarget}
                onClose={() => setDeleteConfirmTarget(null)}
                onConfirm={handleConfirmDeleteFeature}
                title="Удалить объект?"
                message={
                    <>
                        Вы действительно хотите удалить объект <strong>«{deleteConfirmTarget?.properties?.name || deleteConfirmTarget?.id || ''}»</strong>? Это действие необратимо.
                    </>
                }
                confirmLabel="Удалить"
                cancelLabel="Отмена"
                destructive
                loading={isDeletingFeature}
            />

            {/* Модальное окно подтверждения выхода */}
            <ConfirmDialog
                show={isLogoutConfirmOpen}
                onClose={() => setIsLogoutConfirmOpen(false)}
                onConfirm={handleLogout}
                title="Выход из системы"
                message="Вы уверены, что хотите завершить сеанс работы и выйти из панели администратора?"
                confirmLabel="Выйти"
                cancelLabel="Отмена"
                destructive
            />
        </div>
    );
};

const AdminDashboardPage = () => {
    // Оборачиваем панель в провайдеры из модулей (если они есть)
    let content = <AdminDashboardPageInner />;
    
    // Важно: проходим в обратном порядке или учитываем вложенность
    modules.forEach(mod => {
        if (mod.Provider) {
            const Provider = mod.Provider;
            content = <Provider>{content}</Provider>;
        }
    });

    return (
        <AuthGuard>
            {content}
        </AuthGuard>
    );
};

export default AdminDashboardPage;
